import { sanitizeError } from 'src/utils/common.utils'
import {
    getExpertsProfileApi,
    getExportExpertDetailsApi as getExportExpertsDetailsApi,
    getExpertsListApi,
    getListOfCountriesApi,
    getListOfDegreesApi,
    getListOfEducationMajorsApi,
    getListOfLanguagesApi,
    getListOfOccupationsApi,
    getSkillsApi,
} from 'src/http-clients/project.client'
import { EducationType, FilterOptionRespType, FiltersKey, TagType } from 'src/types/expert.types'
import {
    EducationDegreeMapping,
    SkillValidationOptions,
    SkillValidationPriorityOrder,
    SkillLevelPriorityOrder,
    EducationDetailsPriorityOrder,
    LifeCycleStageOption,
    LifeCycleAPIMapping,
    LifeCycleStage,
    YearsOfExperienceRange,
} from 'src/constants/experts.constants'
import {
    ExportDataListRespType,
    ExpertsDataClientReqType,
    ExpertsTableDataRespType,
    SkillFilterDataRespType,
    ExportDetailsRespType,
    ExpertSkillType,
    ExpertsProfileDataRespType,
    ExpertProfileSkillDataRespType,
    ExpertProfileSkillValidatedDataRespType,
    SkillValidationRespType,
    SkillType,
    LanguageListRespType,
    LanguageListClientRespType,
    ExportValidationType,
} from 'src/types/api.types'
import { FilteredTag } from 'src/hooks/useTag'
import { Result } from 'neverthrow'
import { AxiosError } from 'axios'

// eslint-disable-next-line
const fetchFilterApis = async (apiName: () => Promise<Result<any, AxiosError>>, key: string) => {
    try {
        const resp = await apiName()
        if (!resp.isOk()) {
            throw new Error(sanitizeError(resp.error).error.message)
        }
        if (key === 'languages') {
            const uniqueLanguages = new Map<string, LanguageListRespType>()

            resp.value.data?.forEach((lang: LanguageListClientRespType) => {
                if (lang.has_locale && !uniqueLanguages.has(lang.display_name)) {
                    uniqueLanguages.set(lang.display_name, {
                        id: lang.id,
                        label: lang.display_name,
                        value: lang.display_name,
                    })
                }
            })

            return Array.from(uniqueLanguages.values())?.sort((a, b) =>
                a.label.localeCompare(b.label)
            )
        }
        const filterData = resp.value.data?.map((val: { id: string; name: string }) => ({
            id: val.id,
            label: val.name,
            value: val.name,
        }))
        if (key !== 'degress') {
            return filterData?.sort(
                (
                    a: { id: string; label: string; value: string },
                    b: { id: string; label: string; value: string }
                ) => a.label.localeCompare(b.label)
            )
        }
    } catch (error) {
        return []
    }
}

export const getFilterOptions = async (): Promise<FilterOptionRespType> => {
    const filterData = {
        language: await fetchFilterApis(getListOfLanguagesApi, 'languages'),
        location: await fetchFilterApis(getListOfCountriesApi, 'countries'),
        educationMajor: await fetchFilterApis(getListOfEducationMajorsApi, 'majors'),
        occupation: await fetchFilterApis(getListOfOccupationsApi, 'occupations'),
        educationDegree: await fetchFilterApis(getListOfDegreesApi, 'degrees'),
        lifeCycleStage: LifeCycleStageOption,
    }
    return filterData
}

export const getSkills = async (): Promise<SkillFilterDataRespType[]> => {
    const skills = await getSkillsApi()
    if (!skills.isOk()) {
        throw new Error(sanitizeError(skills.error).error.message)
    }

    return skills.value.data
        ?.filter(skill => skill.is_active)
        ?.map((skill: SkillType) => ({
            id: skill.id,
            filterLabel: skill.name,
            levelOptions: skill.levels
                ?.sort((a, b) => a.sequence - b.sequence)
                ?.map(level => ({
                    id: level.id,
                    value: level.level_name,
                    label: level.level_name,
                })),
            skillValidatedOptions: SkillValidationOptions,
        }))
        ?.sort((a, b) => a.filterLabel.localeCompare(b.filterLabel))
}

export const getExpertsList = async (
    reqData: FilteredTag[],
    offset: number,
    limit: number
): Promise<ExpertsTableDataRespType> => {
    const result = getExpertsReqData(reqData, offset, limit)
    const expertsList = await getExpertsListApi(result)
    if (!expertsList.isOk()) {
        throw new Error(sanitizeError(expertsList.error).error.message)
    }
    const response = {
        expertsList: expertsList.value.data.experts
            ?.map(expert => ({
                key: expert.id,
                name: expert.name,
                skills: expert.skills?.map(skill => skill.name),
                location: expert.country,
                degree: getEducationDetailsByPriority(expert.education)[0]?.degree,
                occupation: expert.occupation,
                languages: expert.languages?.join(', '),
                expInYears: expert.years_of_experience,
                lifeCycleStage: getLifeCycleStageKey(expert.lifecycle_stage),
            }))
            ?.sort((a, b) => a.name.localeCompare(b.name)),
        limit: expertsList.value.data.total_count,
    }
    return response
}

const getExpertsReqData = (
    reqData: FilteredTag[],
    offset: number,
    limit: number
): ExpertsDataClientReqType => {
    const tagMap = new Map<string, FilteredTag>()
    reqData?.forEach(tag => {
        if (tag?.key) {
            tagMap.set(tag.key, tag)
        }
    })

    const getTagValue = (key: string) => {
        return (tagMap.get(key)?.id as string)
            ? (tagMap.get(key)?.id as string)
            : tagMap.get(key)?.value ?? undefined
    }

    const getScore = (scoreString?: string) => {
        if (!scoreString) return undefined

        const score = scoreString?.split('/')?.map(Number)
        return { min: score[0] || 0, max: score[1] || 10 }
    }

    const getYearsOfExperience = (value?: string) => {
        if (!value) return undefined

        const years = value?.split('-')?.map(Number)
        return {
            min: years[0] || YearsOfExperienceRange.min,
            max: years[1] || YearsOfExperienceRange.max,
        }
    }

    const getLifeCycleStage = (value?: string) => {
        if (!value) return undefined

        return LifeCycleAPIMapping[value as LifeCycleStage]
    }

    const getSkills = reqData
        ?.filter(tag => tag.type === TagType.skill)
        ?.map(tag => ({
            skill_id: tag.id as string,
            validations:
                tag.filteredLevelIds && tag.filteredLevelIds.length > 0
                    ? tag.filteredLevelIds?.map(levelId => ({
                          level_id: levelId,
                          validation_source_id: SkillValidationOptions?.find(
                              val => val.value === tag.skillIsValidatedBasedOn
                          )?.id,
                          score: getScore(tag.score),
                      }))
                    : undefined,
        }))

    const getExpertiseAreas = reqData
        ?.filter(tag => tag.key === FiltersKey.ExpertiseArea)
        ?.map(tag => tag.value)

    return {
        skills: getSkills.length > 0 ? getSkills : undefined,
        email: getTagValue(FiltersKey.NameAndEmail),
        expertise_areas: getExpertiseAreas.length > 0 ? getExpertiseAreas : undefined,
        languages: getTagValue(FiltersKey.Languages),
        location_id: getTagValue(FiltersKey.Location),
        education_degree_id: getTagValue(FiltersKey.EducationDegree),
        education_major_id: getTagValue(FiltersKey.EducationMajor),
        occupation_id: getTagValue(FiltersKey.Occupation),
        years_of_experience: getYearsOfExperience(getTagValue(FiltersKey.YearsOfExperience)),
        lifecycle_stage: getLifeCycleStage(getTagValue(FiltersKey.LifeCycleStage)),
        offset,
        limit,
    }
}

export const getExportExpertsDetails = async (
    params: string[]
): Promise<ExportDetailsRespType[]> => {
    const respData = await getExportExpertsDetailsApi({ expert_ids: params })
    if (!respData.isOk()) {
        throw new Error(sanitizeError(respData.error).error.message)
    }

    const getSkillValidationDetails = (validations: ExportValidationType[]) => {
        return validations.map(validation => {
            const { validation_type, ...rest } = validation
            delete rest.level
            return {
                ...rest,
                validation_source: validation_type,
            }
        })
    }

    return respData.value.data
        ?.map((expert: ExportDataListRespType) => {
            const expertInfo = {
                authalId: expert.authal_id,
                name: expert.name,
                email: expert.email,
                location: expert.country,
                occupation: expert.occupation,
                languages: expert.languages,
                educationDetails: JSON.stringify(expert.education),
                yearsOfExperience: expert.years_of_experience,
                lifeCycleStage: expert.lifecycle_stage,
                expertiseAreas: expert.areas_of_expertise,
                talentStudioProfile: expert.talent_studio_profile_link,
            }

            return expert.skills.length > 0
                ? expert.skills.map(skill => ({
                      ...expertInfo,
                      skills: skill.name,
                      level: skill.validations[0]?.level || '',
                      skillValidationDetails: JSON.stringify(
                          getSkillValidationDetails(skill.validations)
                      ),
                  }))
                : [
                      {
                          ...expertInfo,
                          skills: '',
                          level: '',
                          skillValidationDetails: '',
                      },
                  ]
        })
        ?.flat(2)
}

const getHighestPriorityValidation = (
    validations: SkillValidationRespType[]
): SkillValidationRespType | undefined => {
    for (const priority of SkillValidationPriorityOrder) {
        const highestPriorityValidation = validations?.find(
            validation => validation.validation_type === priority
        )
        if (highestPriorityValidation !== undefined) {
            return highestPriorityValidation
        }
    }

    return undefined
}

const getSortedValidationsByTime = (
    validations: SkillValidationRespType[]
): ExpertProfileSkillValidatedDataRespType[] =>
    validations
        ?.sort(
            (a, b) =>
                new Date(String(b.updated_at)).getTime() - new Date(String(a.updated_at)).getTime()
        )
        ?.map(validation => ({
            score: validation.score || '--',
            skillValidatedBy: validation.validation_type,
            updatedAt: String(validation.updated_at),
        }))

const getExpertsProfileSkills = (skills: ExpertSkillType[]): ExpertProfileSkillDataRespType[] => {
    if (!skills) {
        return []
    }
    const sortedSkillsWithValidations = skills
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.map(skill => {
            const highestPriorityValidation = getHighestPriorityValidation(skill.validations)
            return {
                skills: skill.name,
                level: skill.validations[0]?.level || '',
                skillValidatedBy: highestPriorityValidation?.validation_type || '',
                score: highestPriorityValidation?.score || '--',
                validationLog: getSortedValidationsByTime(skill.validations),
            }
        })
    const uniqueSkillsMap = new Map<string, ExpertProfileSkillDataRespType>()
    sortedSkillsWithValidations?.forEach(skill => {
        if (!uniqueSkillsMap.has(skill.skills)) {
            uniqueSkillsMap.set(skill.skills, skill)
        }
    })
    return Array?.from(uniqueSkillsMap?.values())
        ?.map(
            skill =>
                sortedSkillsWithValidations
                    ?.filter(item => item.skills === skill.skills)
                    ?.sort(
                        (a, b) =>
                            SkillLevelPriorityOrder.indexOf(a.level) -
                            SkillLevelPriorityOrder.indexOf(b.level)
                    )
        )
        ?.flat()
}

const getEducationDetailsByPriority = (education: EducationType[]) => {
    if (!education) {
        return []
    }
    return EducationDetailsPriorityOrder?.map(
        degree => education?.filter(item => item.degree === degree)
    )
        ?.flat()
        ?.map(item => ({
            ...item,
            degree: EducationDegreeMapping[item.degree],
        }))
}

const getLifeCycleStageKey = (lifecycleStage: string): string => {
    for (const [key, value] of Object.entries(LifeCycleAPIMapping)) {
        if (value === lifecycleStage) {
            return key
        }
    }
    return ''
}

export const getExpertProfileData = async (
    clientId: string
): Promise<ExpertsProfileDataRespType> => {
    const respData = await getExpertsProfileApi(clientId)
    if (!respData.isOk()) {
        throw new Error(sanitizeError(respData.error).error.message)
    }

    const profile = respData.value.data
    return {
        expertDetails: {
            name: profile.name,
            email: profile.email,
            country: profile.country,
            occupation: profile.occupation,
            yearsOfExperience: profile.years_of_experience + ' years',
            language: profile.languages,
            education: getEducationDetailsByPriority(profile.education),
            lifeCycleStage: getLifeCycleStageKey(profile.lifecycle_stage),
        },
        talentStudioProfileLink: profile.talent_studio_profile_link,
        areasOfExpertise: profile.areas_of_expertise,
        skills: getExpertsProfileSkills(profile.skills),
    }
}
